import i18next from 'i18next';
import type { Key } from 'react';
import type { UseFormRegister, UseFormRegisterReturn } from 'react-hook-form';
import type { Observable } from 'rxjs';
import { forkJoin, take } from 'rxjs';
import type { AnySchema, Lazy, Reference } from 'yup';
import * as yup from 'yup';
import { forceAssert, isObject } from '../common-functions';
import type { CheckboxChangeEventType } from '../fields/checkbox/checkbox.component';
import type { RadioGroupResult } from '../fields/radio-group/radio-group.component';
import type { JsonData, JsonDataItem } from '../generic-types';
import { isEmptyTiptapHtml } from '../rich-text/is-empty-content';
import { DynamicFormRequiredType } from './dynamic-form.enums';
import type { DynamicFormField } from './dynamic-form.interfaces';

export function isFieldHidden(field: DynamicFormField) {
  return field.hidden || field.permissions?.read === false;
}

export function hasRegisterMethod(settings: Record<string, unknown>) {
  return 'register' in settings && typeof settings['register'] === 'function' && settings['register'].length === 2;
}

export function isHookFormRegisterFunction(register: unknown): register is UseFormRegister<Record<string, unknown>> {
  return typeof register === 'function' && register.length === 2;
}

export function isCheckboxChangeEventType(item: unknown): item is CheckboxChangeEventType {
  return (
    isObject(item) && Object.hasOwn(item, 'name') && Object.hasOwn(item, 'value') && Object.hasOwn(item, 'checked')
  );
}

export function applyExtraProperties(settings: Record<string, unknown>): Record<string, unknown> {
  let hfSettings: Record<string, unknown> = {};

  if (
    'name' in settings &&
    typeof settings.name === 'string' &&
    hasRegisterMethod(settings) &&
    isHookFormRegisterFunction(settings.register)
  ) {
    const { ref: inputRef, ...registerProps }: UseFormRegisterReturn = settings.register(settings.name);
    hfSettings = {
      inputRef,
      ...registerProps,
    };
  }

  return {
    ...settings,
    ...hfSettings,
  };
}

export function getFieldSettingByName(
  fieldName: string,
  fields: Array<DynamicFormField>
): DynamicFormField | undefined {
  for (let i = fields.length; i-- !== 0; ) {
    const currentField = fields[i];
    if (currentField.fieldName === fieldName) {
      return currentField;
    } else if (currentField.fields) {
      const childSetting = getFieldSettingByName(fieldName, currentField.fields);
      if (childSetting) {
        return childSetting;
      }
    }
  }

  return undefined;
}

export function getFieldSettingById(fieldId: Key, fields: Array<DynamicFormField>): DynamicFormField | undefined {
  for (let i = fields.length; i-- !== 0; ) {
    const currentField = fields[i];
    if (currentField.id.toString() === fieldId.toString()) {
      return currentField;
    } else if (currentField.fields) {
      const childSetting = getFieldSettingById(fieldId, currentField.fields);
      if (childSetting) return childSetting;
    }
  }

  return undefined;
}

/**
 * Gets the parent field ids in the field settings.
 * @param {Array<DynamicFormField>} fields - The fields to search
 * @returns {Array<string>} The found parent field ids.
 */
export function getParentFieldIds(fields: Array<DynamicFormField>): Array<string> {
  const parentFieldIds: Array<string> = [];
  for (let i = 0, length = fields.length; i < length; i++) {
    const field = fields[i];
    if (field.parentFieldId) {
      parentFieldIds.push(field.parentFieldId);
    }

    if (field.fields) {
      const childFieldIds = getParentFieldIds(field.fields);
      parentFieldIds.push(...childFieldIds);
    }
  }

  return parentFieldIds;
}

export function buildParentFieldMap(fields: Array<DynamicFormField>, parentFields = new Map<string, Array<string>>()) {
  for (let i = fields.length; i-- !== 0; ) {
    const field = fields[i];
    if (field.parentFieldId) {
      if (parentFields.has(field.parentFieldId)) {
        parentFields.get(field.parentFieldId)?.push(field.fieldName);
      } else {
        parentFields.set(field.parentFieldId, [field.fieldName]);
      }
    }
    const parentCompetencyField = (field.dynamicProperties?.defaultFieldConfiguration as Record<string, unknown>)
      ?.parentCompetencyField as Record<string, unknown>;
    if (parentCompetencyField?.id) {
      const stringValue = String(parentCompetencyField.id);
      if (parentFields.has(stringValue)) {
        parentFields.get(stringValue)?.push(field.fieldName);
      } else {
        parentFields.set(stringValue, [field.fieldName]);
      }
    }
    if (field.fields) {
      buildParentFieldMap(field.fields, parentFields);
    }
  }

  return parentFields;
}

export function isDataLoadedField(field: DynamicFormField) {
  return (
    field.fieldType === 'COMBOBOX' ||
    field.fieldType === 'MULTISELECTFIELD' ||
    field.fieldType === 'DROPDOWNMULTISELECT' ||
    field.fieldType === 'OPTIONGROUP' ||
    field.fieldType === 'MULTISELECTCHECKBOX' ||
    field.fieldType === 'TWIN_COLUMN_SELECT' ||
    field.fieldType === 'OPTION_BUILDER'
  );
}

export function getPreSubmitData(observables: Array<Observable<JsonDataItem>>) {
  return new Promise<JsonData>((resolve) => {
    if (observables.length > 0) {
      forkJoin(observables)
        .pipe(take(1))
        .subscribe((result) => resolve(result));
    } else {
      resolve([]);
    }
  });
}

/**
 * Returns the formatted required field error message.
 *
 * This is a convenience method, that should be used when validating required fields.
 *
 * @param label the field label, which is used as part of the error message.
 *
 * @returns the error message
 */
export const fieldValidationErrorMessage = (label: string) =>
  i18next.t('required-field', { label: label }) ?? `'${label}' is a required field`;

export function getValidationObjectByFieldType(field: DynamicFormField) {
  const { caption: fieldLabel, fieldType, validationFunc } = field;

  switch (fieldType) {
    case 'DATEFIELD': {
      if (shouldBeValidated(field.required)) {
        return yup.date().required(fieldValidationErrorMessage(fieldLabel));
      } else {
        return yup.date().nullable();
      }
    }
    case 'TIMEFIELD':
      return yup.string().required(fieldValidationErrorMessage(fieldLabel));
    case 'DOUBLEFIELD':
    case 'INTFIELD':
    case 'TEXTFIELD':
      return yup.string().required(fieldValidationErrorMessage(fieldLabel));
    case 'RICHTEXTAREA':
      return yup
        .string()
        .required(fieldValidationErrorMessage(fieldLabel))
        .test(
          'has-user-value',
          () => fieldValidationErrorMessage(fieldLabel),
          (value) => !isEmptyTiptapHtml(value)
        );
    case 'HISTORY':
      return yup
        .array()
        .of(
          yup.object().shape({
            message: yup.string().required(),
            draft: yup.boolean().notRequired(),
            date: yup.string().notRequired(),
            user: yup.string().notRequired(),
            userId: yup.string().notRequired(),
          })
        )
        .required(() => fieldValidationErrorMessage(fieldLabel))
        .test(
          'has-draft-value',
          () => fieldValidationErrorMessage(fieldLabel),
          (value) => Array.isArray(value) && value.some((item) => item.draft === true)
        );
    case 'TEXTAREA':
      return yup
        .string()
        .required(fieldValidationErrorMessage(fieldLabel))
        .test(
          'length',
          () => fieldValidationErrorMessage(fieldLabel),
          (value) => (value ? value.length > 0 : false)
        );
    case 'SIGNATURECAPTURE':
      return yup
        .string()
        .required(fieldValidationErrorMessage(fieldLabel))
        .test(
          'length',
          () => fieldValidationErrorMessage(fieldLabel),
          (value) => (value ? value.length > 0 : false)
        );
    case 'COMBOBOX':
      return yup
        .object()
        .required(fieldValidationErrorMessage(fieldLabel))
        .test(
          'has-value',
          () => fieldValidationErrorMessage(fieldLabel),
          (value) => (value ? 'text' in value && typeof value.text === 'string' : false)
        );
    case 'OPTIONGROUP':
      return yup.object<RadioGroupResult>().required(fieldValidationErrorMessage(fieldLabel));
    case 'MULTISELECTCHECKBOX':
      return yup
        .array()
        .required(fieldValidationErrorMessage(fieldLabel))
        .min(1, fieldValidationErrorMessage(fieldLabel));
    case 'MULTISELECTFIELD':
      return yup
        .array()
        .required(fieldValidationErrorMessage(fieldLabel))
        .min(1, fieldValidationErrorMessage(fieldLabel));
    case 'DROPDOWNMULTISELECT':
      return yup
        .array()
        .required(fieldValidationErrorMessage(fieldLabel))
        .min(1, fieldValidationErrorMessage(fieldLabel))
        .label(fieldLabel);
    case 'CHECKBOX':
      return yup
        .boolean()
        .required(fieldValidationErrorMessage(fieldLabel))
        .oneOf([true], fieldValidationErrorMessage(fieldLabel))
        .label(fieldLabel);
    case 'TWIN_COLUMN_SELECT':
      return yup
        .array()
        .required(fieldValidationErrorMessage(fieldLabel))
        .min(1, fieldValidationErrorMessage(fieldLabel))
        .label(fieldLabel);
    case 'CUSTOM':
      return validationFunc;
    default:
      return validationFunc;
  }
}

/**
 * Returns all required fields.
 *
 * If a field is hidden it will not be included in the resulting array if it is required.
 * To include required hidden fields to the resulting array, call with `includeHidden` set to `true`.
 *
 * @param fields{Array<DynamicFormField>} - The dynamic form fields definition
 * @param idField{string} - The setting field used for the validation key.
 * @param includeHidden{boolean} - Specifies if hidden fields should be included in the required fields array. Defaults to `false`.
 */
export function getRequiredFields(fields: Array<DynamicFormField>, idField = 'fieldName', includeHidden = false) {
  const requiredFields: Record<string, AnySchema | Reference | Lazy<AnySchema, unknown>> = {};
  for (let i = 0, length = fields.length; i < length; i++) {
    const field = fields[i];
    if (field.fieldType === 'GROUP' && field.permissions?.edit) {
      if (field.fields) {
        for (let j = 0, gLength = field.fields.length; j < gLength; j++) {
          const groupField = field.fields[j];
          const isHidden =
            field.hidden === true ||
            field.permissions?.read === false ||
            groupField.hidden === true ||
            groupField.permissions?.read === false;
          const shouldIncludeHidden = includeHidden || !isHidden;
          const validationObject = getValidationObjectByFieldType(groupField);
          if (groupField && shouldBeValidated(groupField.required) && shouldIncludeHidden && validationObject) {
            requiredFields[String(forceAssert<Record<string, unknown>>(groupField)[idField])] = validationObject;
          }
        }
      }
    } else {
      const isHidden = field.hidden === true || field.permissions?.read === false;
      const shouldIncludeHidden = includeHidden || !isHidden;
      const validationObject = getValidationObjectByFieldType(field);
      if (field.permissions?.edit && shouldBeValidated(field.required) && shouldIncludeHidden && validationObject) {
        requiredFields[String(forceAssert<Record<string, unknown>>(field)[idField])] = validationObject;
      }
    }
  }

  return requiredFields;
}

const shouldBeValidated = (required?: DynamicFormRequiredType) => {
  return required === DynamicFormRequiredType.True || required === DynamicFormRequiredType.Partial;
};
